import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * A single kitchen timer, held in a module store so it keeps running as you move between
 * views and survives a page reload (the wall might get navigated or refreshed mid-boil).
 *
 * Per-device, not shared — a timer is "my eggs", not household state — so it lives in
 * localStorage rather than on the server.
 */

const KEY = 'pacer.timer';

interface TimerState {
  /** Epoch ms when the timer fires, or null when idle. */
  endsAt: number | null;
  /** The duration it was started with, for the "restart" affordance. */
  durationMs: number;
  label: string;
}

function load(): TimerState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as TimerState;
  } catch {
    /* ignore */
  }
  return { endsAt: null, durationMs: 0, label: '' };
}

let state: TimerState = load();
const listeners = new Set<() => void>();

function set(next: TimerState): void {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* non-persistent still works this session */
  }
  for (const l of listeners) l();
}

export function startTimer(durationMs: number, label = ''): void {
  set({ endsAt: Date.now() + durationMs, durationMs, label });
}

export function stopTimer(): void {
  set({ endsAt: null, durationMs: state.durationMs, label: state.label });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  const onStorage = (e: StorageEvent): void => {
    if (e.key === KEY) {
      state = load();
      l();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

export function useTimerState(): TimerState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/**
 * Live remaining milliseconds, ticking ~4×/sec while running. `justFinished` flips true for
 * the single render where the timer crosses zero, so the caller can chime exactly once.
 */
export function useTimer(): { endsAt: number | null; remainingMs: number; running: boolean; justFinished: boolean } {
  const { endsAt } = useTimerState();
  const [now, setNow] = useState(() => Date.now());
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (endsAt === null) {
      setFinished(false);
      return;
    }
    const tick = (): void => setNow(Date.now());
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [endsAt]);

  const remainingMs = endsAt === null ? 0 : Math.max(0, endsAt - now);
  const running = endsAt !== null && remainingMs > 0;

  // Detect the zero-crossing without spamming: fire once, then latch until reset/restart.
  const crossed = endsAt !== null && remainingMs === 0;
  useEffect(() => {
    if (crossed && !finished) setFinished(true);
    if (endsAt === null && finished) setFinished(false);
  }, [crossed, finished, endsAt]);

  return { endsAt, remainingMs, running, justFinished: crossed && !finished };
}

export function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** A short chime via WebAudio — no asset to ship, works offline. */
export function playChime(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const notes = [880, 1108, 1318];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.4, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.42);
    });
    setTimeout(() => void ctx.close(), 1500);
  } catch {
    /* audio unavailable — the visual banner still fires */
  }
}
